/**
 * H①-native — drives HealthKit daily-aggregate sync from the shell.
 *
 * Renders nothing; mounted once at the app root (beside BottomTab /
 * CoachFab) so it lives across the whole signed-in native session.
 *
 * Triggers (contract §5 H①-native): app open (this mount) + every
 * foreground, gated on native + signed-in + the `health_context_v1`
 * kill switch. The post-run-save trigger fires from run-spike directly.
 * All best-effort/no-throw (syncHealthMetrics swallows every failure).
 */

import { useEffect } from 'react';
import { useAuthContext } from '../../contexts/AuthContext';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import { useServerFlag } from '../../hooks/useServerFlag';
import { isNativeApp } from '../../lib/platform';
import { syncHealthMetrics } from '../../lib/health/healthKit';

export function HealthSyncMount(): null {
  const { user } = useAuthContext();
  const fetchAuthed = useAuthFetch();
  const healthOn = useServerFlag('health_context_v1');

  useEffect(() => {
    if (!isNativeApp() || !user || !healthOn) return;
    let alive = true;

    // App open (this mount) = the first foreground sync.
    void syncHealthMetrics(fetchAuthed);

    // Warm foregrounds via @capacitor/app's appStateChange.
    let remove: (() => void) | null = null;
    void (async () => {
      try {
        const { App } = await import('@capacitor/app');
        const handle = await App.addListener('appStateChange', ({ isActive }) => {
          if (isActive && alive) void syncHealthMetrics(fetchAuthed);
        });
        remove = () => void handle.remove();
      } catch {
        /* @capacitor/app unavailable — the mount-time sync still ran. */
      }
    })();

    return () => {
      alive = false;
      remove?.();
    };
  }, [user, healthOn, fetchAuthed]);

  return null;
}
